import { DealParams } from 'app/models/deal-params'
import { EntityParams } from 'app/models/entity-params'
import {
    FacilityAmountsParams,
    FacilityParams,
} from 'app/models/facility-params'
import { MasterTradeParams } from 'app/models/master-trade-params'
import { SubTradeParams } from 'app/models/sub-trade-params'
import {
    RefinedSubTrade,
    TradedFacilityParams,
} from 'app/models/traded-facilities-params'
import { WireInstructionParams } from 'app/models/wire-instruction-params'
import { calculateFee } from 'app/state/ducks/traded-facilities/thunks'
import {
    addOneWorkingDay,
    businessDaysDiffWithHolidays,
    computeFeeTotals,
    formatDateToShow,
    stringToDate,
} from 'app/utils/util-functions'
import moment from 'moment'
import { HolidayCalendarParams } from 'app/models/holiday-calendar-params'
import { AAMetadataResponse } from 'app/models/aa-metadata-params'
import { FMFacilityAmounts } from 'app/models/fm-metadata-params'

export const showTradeId = (value: string) => {
    const valuesToShow = ['VTS', 'CP']
    if (valuesToShow.includes(value)) {
        return true
    }
    return false
}

export const resolveTradeHolidayDates = (
    tradeAssociation: string | null | undefined,
    calendars: HolidayCalendarParams[],
): string[] => {
    switch (tradeAssociation) {
        case 'LSTA': {
            const cal = calendars?.find(
                c => c.lstaTradeCalcs && c.status === 'Active',
            )
            return cal?.holidayInfo.map(h => h.holidayDate) ?? []
        }
        case 'LMA': {
            const cal = calendars?.find(
                c => c.lmaTradeCalcs && c.status === 'Active',
            )
            return cal?.holidayInfo.map(h => h.holidayDate) ?? []
        }
        default:
            return []
    }
}

export const getDisplayedSettlementDate = (
    subTrade: { lockedDate?: string | null; targetSettlementDate: string | null },
    masterTrade: Pick<MasterTradeParams, 'tradeAssociation'>,
    businessDate: Date,
    holidayCalendars: HolidayCalendarParams[],
): Date => {
    if (subTrade.lockedDate) {
        return stringToDate(subTrade.lockedDate)
    }

    const holidays = resolveTradeHolidayDates(masterTrade.tradeAssociation, holidayCalendars)

    const targetSettlementDate = stringToDate(subTrade.targetSettlementDate ?? '')

    if (businessDate > targetSettlementDate) {
        const nextBusinessDate = addOneWorkingDay(moment(businessDate), holidays)
        return nextBusinessDate.toDate()
    }

    return targetSettlementDate
}

export const getRefinedSubTrades = (
    masterTrade: MasterTradeParams,
    subTrades: SubTradeParams[],
    allDeals: DealParams[],
    allEntities: EntityParams[],
    allTradedFacilities: TradedFacilityParams[],
    allFacilities: FacilityParams[],
    businessDate: Date,
    calendar: HolidayCalendarParams[],
    wireInstructions: WireInstructionParams[] = [],
    aaMetadata?: AAMetadataResponse,
    isLoadingAAMetadata?: boolean,
) => {
    const holidays = resolveTradeHolidayDates(masterTrade.tradeAssociation, calendar)
    return subTrades
        .filter(st => st.masterTradeId.id === masterTrade.id)
        .map(trade => {
            const matchingDeal = allDeals.find(
                deal => deal.id === masterTrade.dealId?.id,
            )
            const matchingSeller = allEntities.find(e => e.id === trade.sellerId?.id)
            const matchingBuyer = allEntities.find(e => e.id === trade.buyerId?.id)
            const subTradeFacilities = allTradedFacilities
                .filter(tf => tf.contractId?.id === trade.id)
                .map(facility => ({
                    ...facility,
                    facilityDetails:
                        allFacilities.find(f => f.id === facility.facilityId.id) || null,
                }))
            const momentTPlus = trade.lockedDate
                ? moment(trade.lockedDate)
                : moment(businessDate)
            const displayedSettlementDate = getDisplayedSettlementDate(
                trade,
                masterTrade,
                businessDate,
                calendar,
            )

            const aaDoc = aaMetadata?.data?.find(
                (doc: any) => doc.subTradeId === trade.systemId,
            )
            const shouldUseAADate =
                trade.status === 'Open' && aaDoc?.targetSettlementDate
            const finalSettlementDate =
                shouldUseAADate && aaDoc?.targetSettlementDate
                    ? stringToDate(aaDoc.targetSettlementDate)
                    : displayedSettlementDate

            // Transform wire instruction IDs to full objects which are needed for validation
            const wireInstructionMap = new Map(
                wireInstructions.map(wi => [wi.id, wi]),
            )

            // Get wire instructions for buyer and seller entities
            const buyerEntityWireInstructions = wireInstructions.filter(
                instruction =>
                    matchingBuyer &&
                    instruction.entity?.id &&
                    instruction.entity.id === matchingBuyer.id,
            )
            const sellerEntityWireInstructions = wireInstructions.filter(
                instruction =>
                    matchingSeller &&
                    instruction.entity?.id &&
                    instruction.entity.id === matchingSeller.id,
            )

            // Use trade-level wire instructions if available, otherwise use entity-level
            const populatedBuyerWireInstructions: WireInstructionParams[] =
                trade.buyerWireInstructions?.length > 0
                    ? (trade.buyerWireInstructions
                        .map((wi: any) => {
                            const wiId = typeof wi === 'string' ? wi : wi.id
                            const found = wireInstructionMap.get(wiId)
                            return found ?? wi
                        })
                        .filter(
                            (wi: any) => wi && typeof wi === 'object' && 'entityName' in wi,
                        ) as WireInstructionParams[])
                    : buyerEntityWireInstructions

            const populatedSellerWireInstructions: WireInstructionParams[] =
                trade.sellerWireInstructions?.length > 0
                    ? (trade.sellerWireInstructions
                        .map((wi: any) => {
                            const wiId = typeof wi === 'string' ? wi : wi.id
                            const found = wireInstructionMap.get(wiId)
                            return found ?? wi
                        })
                        .filter(
                            (wi: any) => wi && typeof wi === 'object' && 'entityName' in wi,
                        ) as WireInstructionParams[])
                    : sellerEntityWireInstructions

            // Get agent entity from the first facility's admin entity
            const matchingAgent =
                subTradeFacilities.length > 0
                    ? allEntities.find(
                        entity =>
                            entity.id ===
                            subTradeFacilities[0]?.facilityDetails?.adminEntity?.id,
                    )
                    : null

            // Get wire instructions for agent entity
            const agentWireInstructions = wireInstructions.filter(
                instruction =>
                    matchingAgent &&
                    instruction.entity?.id &&
                    instruction.entity.id === matchingAgent.id,
            )

            // Calculate assignment fee amount from the deal
            const assignmentFeeAmount = matchingDeal?.assignmentFee
                ? Number(matchingDeal.assignmentFee)
                : 0

            return {
                ...trade,
                sellerEntity: matchingSeller || null,
                buyerEntity: matchingBuyer || null,
                tradedFacilities: subTradeFacilities,
                masterTrade: masterTrade,
                targetSettlementDate: formatDateToShow(finalSettlementDate),
                tPlus: businessDaysDiffWithHolidays(
                    moment(masterTrade.tradeDate),
                    momentTPlus,
                    holidays,
                ),
                dealCusip: matchingDeal ? matchingDeal.cusip : 'N/A',
                dealName: matchingDeal ? matchingDeal.dealName : 'N/A',
                populatedBuyerWireInstructions: populatedBuyerWireInstructions,
                populatedSellerWireInstructions: populatedSellerWireInstructions,
                agentWireInstructions: agentWireInstructions,
                assignmentFeeAmount: assignmentFeeAmount,
                isLoadingAAMetadata: isLoadingAAMetadata && trade.status === 'Open',
            }
        })
}



/**
 * Calculate the applied PIK amount between trade date and settlement date
 * PIK that occurs after trade date "travels free" per LSTA Section 5
 * and should be excluded from purchase price calculations
 */
export const getAppliedPikAmount = (
    amounts: { amount: string; effectiveDate: string; transaction: string }[] | undefined,
    tradeDate: string | undefined,
    settlementDate: string | undefined,
): number => {
    if (!amounts || !tradeDate || !settlementDate) return 0

    let appliedPikAmount = 0
    const pikTransactions = amounts.filter(
        amt =>
            amt.transaction === 'PIKTradeTransaction' &&
            new Date(amt.effectiveDate) >= new Date(tradeDate) &&
            new Date(amt.effectiveDate) <= new Date(settlementDate),
    )
    pikTransactions.forEach(pikAmt => {
        const pikIdx = amounts.indexOf(pikAmt)
        if (pikIdx > 0) {
            const prevAmount = amounts[pikIdx - 1]
            appliedPikAmount += Number(pikAmt.amount) - Number(prevAmount?.amount ?? 0)
        }
    })
    return appliedPikAmount
}

export const getFundedAndUnfundedTradeAmount = (
    amount: number,
    price: number,
    effectiveDate: Date | null,
    facilityAmounts: FMFacilityAmounts[],
    facilityId: string,
) => {
    const singleFacilityAmounts = facilityAmounts.find(
        fa => fa.facilityId.id === facilityId,
    )
    const singleFacilityAmount = singleFacilityAmounts
        ? effectiveDate
            ? (singleFacilityAmounts.facilityAmounts.findLast(
                amountWithDate =>
                    new Date(amountWithDate.effectiveDate) <= effectiveDate,
            ) ??
                singleFacilityAmounts.facilityAmounts[
                singleFacilityAmounts.facilityAmounts.length - 1
                ])
            : singleFacilityAmounts.facilityAmounts[
            singleFacilityAmounts.facilityAmounts.length - 1
            ]
        : null

    const facilityAmount = Number(singleFacilityAmount?.amount ?? 0)
    const fundedProportion = singleFacilityAmount && facilityAmount > 0
        ? ((Number(singleFacilityAmount.loanUtilizedAmount) +
            Number(singleFacilityAmount.locUtilizedAmount)) /
            facilityAmount) *
        100.0
        : 100
    const unfundedProportion = 100.0 - fundedProportion
    const fundendTradeAmount = (amount * fundedProportion) / 100.0
    const unfundedTradeAmount = (amount * unfundedProportion) / 100.0
    const fundedAmount = (fundendTradeAmount * Number(price)) / 100.0
    const unfundedAmount =
        (-1 * unfundedTradeAmount * (100 - Number(price))) / 100.0
    return { fundedAmount, unfundedAmount }
}

export const getTotalPurchaseAmountTable = (
    refinedSubTrade: RefinedSubTrade,
    wireInstructions: WireInstructionParams[],
    facilityAmounts: FacilityAmountsParams[],
) => {
    const tradeDate = refinedSubTrade.masterTrade.tradeDate
    const settlementDate = refinedSubTrade.targetSettlementDate

    const tableData = Object.values(
        refinedSubTrade?.tradedFacilities?.reduce<
            Record<
                string,
                {
                    id: number
                    currency: string
                    amount: number
                    sellerWireInstructions: string
                    buyerWireInstructions: string
                }
            >
        >((acc, facility, index) => {
            const currency = facility.facilityDetails?.baseCurrency ?? 'USD'
            const currentAmount =
                refinedSubTrade.masterTrade.paymentType === 'Standard' &&
                    facility.amounts
                    ? refinedSubTrade.lockedDate
                        ? Number(
                            facility.amounts?.findLast(
                                amountWithDate =>
                                    new Date(amountWithDate.effectiveDate) <=
                                    new Date(refinedSubTrade.lockedDate ?? ''),
                            )?.amount ?? '0.0',
                        )
                        : Number(facility.amounts[facility.amounts.length - 1]?.amount ?? 0)
                    : 0

            // PIK travels free: calculate pro-rata on full amount first, then subtract PIK
            // Correct formula: price × (fundedPrincipal - PIK)  NOT: price × (amount - PIK) × fundedProportion
            // For Cashless/Elevation trades, no cash changes hands so PIK deduction is irrelevant
            const appliedPikAmount = refinedSubTrade.masterTrade.paymentType === 'Standard'
                ? getAppliedPikAmount(facility.amounts, tradeDate ?? undefined, settlementDate)
                : 0

            const { fundedAmount: rawFundedAmount, unfundedAmount } = getFundedAndUnfundedTradeAmount(
                currentAmount,
                Number(facility.price),
                refinedSubTrade.lockedDate
                    ? new Date(refinedSubTrade.lockedDate)
                    : refinedSubTrade.targetSettlementDate
                        ? new Date(refinedSubTrade.targetSettlementDate)
                        : null,
                facilityAmounts as FMFacilityAmounts[],
                facility.facilityId.id,
            )
            // Subtract PIK after pro-rata: fundedAmount = rawFundedAmount - PIK × price/100
            const fundedAmount = rawFundedAmount - (appliedPikAmount * Number(facility.price) / 100)
            if (!acc[currency]) {
                const wireInstructionMap = new Map(
                    wireInstructions.map(wi => [wi.id, wi]),
                )

                refinedSubTrade.sellerWireInstructions =
                    refinedSubTrade.sellerWireInstructions
                        .map(wi => wireInstructionMap.get(wi.id) ?? wi)
                        .filter((wi): wi is WireInstructionParams => 'entityName' in wi)

                refinedSubTrade.buyerWireInstructions =
                    refinedSubTrade.buyerWireInstructions
                        .map(wi => wireInstructionMap.get(wi.id) ?? wi)
                        .filter((wi): wi is WireInstructionParams => 'entityName' in wi)

                acc[currency] = {
                    id: index,
                    currency,
                    amount: fundedAmount + unfundedAmount,
                    sellerWireInstructions: refinedSubTrade.sellerWireInstructions.find(
                        wi => wi.currency === currency,
                    )
                        ? 'Yes'
                        : 'No',
                    buyerWireInstructions: refinedSubTrade.buyerWireInstructions.find(
                        wi => wi.currency === currency,
                    )
                        ? 'Yes'
                        : 'No',
                }
            } else {
                acc[currency].amount += fundedAmount + unfundedAmount
            }
            return acc
        }, {}) || {},
    )

    // Sort descending by currency name
    return tableData.sort((a, b) => b.currency.localeCompare(a.currency))
}

export const getFeeTotalsArray = (refinedSubTrade: RefinedSubTrade) => {
    const fees = refinedSubTrade.fees || []
    const feeTotals = computeFeeTotals(fees)
    return Object.entries(feeTotals).map(([currency, amount]) => ({
        currency,
        amount,
    }))
}

export const getTradeFacilityWithCalculatedFee = async (
    refinedSubTrade: RefinedSubTrade,
) => {
    return await Promise.all(
        refinedSubTrade.tradedFacilities.map(async tf => {
            const result: any[] = await calculateFee(
                tf.facilityId.id,
                tf.contractId?.id ?? '',
            )

            const newFees = tf.fees.map(fee => {
                const calculatedFee = result.find(
                    calculatedFee => calculatedFee.feeType === fee.feeType,
                )
                if (calculatedFee) {
                    if (refinedSubTrade.masterTrade.paymentType !== 'Standard') {
                        return {
                            ...fee,
                            otherAmount:
                                fee.overrideAmount && fee.overrideAmount != ''
                                    ? '0.0'
                                    : undefined,
                            overrideAmount:
                                fee.overrideAmount && fee.overrideAmount != ''
                                    ? fee.overrideAmount
                                    : '0.0',
                        }
                    }
                    return {
                        ...fee,
                        otherAmount:
                            fee.overrideAmount && fee.overrideAmount != ''
                                ? calculatedFee.amount
                                : undefined,
                        overrideAmount:
                            fee.overrideAmount && fee.overrideAmount != ''
                                ? fee.overrideAmount
                                : calculatedFee?.amount,
                    }
                }

                return {
                    ...fee,
                    otherAmount: undefined,
                    overrideAmount:
                        fee.overrideAmount && fee.overrideAmount != ''
                            ? fee.overrideAmount
                            : '0.0',
                }
            })
            return {
                ...tf,
                fees: newFees,
            }
        }),
    )
}

export const getTradeFacilityFeeTotalsArray = (
    tradedFacilityWithCalculatedFee: TradedFacilityParams[],
) => {
    const currencyArray: Record<string, number> = {}
    tradedFacilityWithCalculatedFee.forEach(tf => {
        const currency = tf.facilityDetails?.baseCurrency ?? 'USD'
        const total = tf.fees.reduce((acc, fee) => {
            const amount = Number(fee.overrideAmount)
            return fee.paidTo === 'PaidToSeller'
                ? acc + Number(amount)
                : acc - Number(amount)
        }, 0)
        currencyArray[currency] = (currencyArray[currency] || 0) + total
    })
    return currencyArray
}

